import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getEmptyAddress } from '../../../../interfaces/Address/Address';
import {
  createAddress,
  getAddressByID,
  updateAddress,
} from '../../../../api/Address';
import { bodyWidth } from '../../../../utils/Style';
import { useThemeColor } from '../../../../hooks/useThemeColor';
import { cities, statesByCity } from '../../../../constants/Address';
import { GenericHelmet } from '../../../../components/Helmet/GenericHelmet';
import { useLocalizedText } from '../../../../hooks/useLocalizedText';

export const ModifyAddressPage = () => {
  const t = useLocalizedText();

  const navigate = useNavigate();

  const { primary, secondary } = useThemeColor();

  const { addressID: addressIDAsStr } = useParams();

  const addressID = addressIDAsStr ? Number(addressIDAsStr) : undefined;
  const isEditing = Number.isFinite(addressID);

  const [address, setAddress] = useState(getEmptyAddress());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      getAddressByID(addressID!)
        .then(setAddress)
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  }, [addressID]);

  const handleSave = () => {
    const mutation = isEditing ? updateAddress : createAddress;
    setLoading(true);
    mutation(address)
      .then(() => {
        toast.success(
          isEditing
            ? t('address.modify.update_successful')
            : t('address.modify.create_successful'),
        );
        navigate('/hesabim/adres');
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const isFirstNameValid = address.firstName.length > 0;
  const isLastNameValid = address.lastName.length > 0;
  const isAddressValid = address.address1.length > 0;
  const isTaxNoValid = address.addressType !== '1' || !!address.taxNo?.length;
  const isTaxOfficeValid =
    address.addressType !== '1' || !!address.taxOffice?.length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <GenericHelmet
        title={
          isEditing
            ? t('address.modify.edit_address')
            : t('address.modify.create_address')
        }
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          py: 3,
          px: 1.5,
          gap: 1.5,
          boxSizing: 'border-box',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            px: 2.5,
            py: 2,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            maxWidth: 800,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <IconButton onClick={() => navigate('/hesabim/adres')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
              {isEditing
                ? t('address.modify.edit_address')
                : t('address.modify.create_address')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                width: '100%',
              }}
            >
              <Typography>{t('address.name')}</Typography>

              <TextField
                size='small'
                fullWidth
                disabled={loading}
                value={address.firstName}
                onChange={(e) =>
                  setAddress({ ...address, firstName: e.target.value })
                }
                error={!isFirstNameValid}
                helperText={
                  isFirstNameValid
                    ? undefined
                    : t('validation.field_is_required')
                }
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                width: '100%',
              }}
            >
              <Typography>{t('address.surname')}</Typography>

              <TextField
                size='small'
                fullWidth
                disabled={loading}
                value={address.lastName}
                onChange={(e) =>
                  setAddress({ ...address, lastName: e.target.value })
                }
                error={!isLastNameValid}
                helperText={
                  isLastNameValid
                    ? undefined
                    : t('validation.field_is_required')
                }
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                width: '100%',
              }}
            >
              <Typography>{t('address.city')}</Typography>

              <Select
                size='small'
                fullWidth
                required
                value={address.city}
                onChange={(e) => {
                  const city = e.target.value;

                  setAddress({
                    ...address,
                    city,
                    state: statesByCity[city][0],
                  });
                }}
              >
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                width: '100%',
              }}
            >
              <Typography>{t('address.province')}</Typography>

              <Select
                size='small'
                fullWidth
                required
                value={address.state}
                onChange={(e) => {
                  setAddress({
                    ...address,
                    state: e.target.value,
                  });
                }}
              >
                {statesByCity[address.city].map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>{t('address.address')}</Typography>

            <TextField
              size='small'
              fullWidth
              multiline
              minRows={3}
              disabled={loading}
              value={address.address1}
              onChange={(e) =>
                setAddress({ ...address, address1: e.target.value })
              }
              error={!isAddressValid}
              helperText={
                isAddressValid ? undefined : t('validation.field_is_required')
              }
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>{t('address.zip_code')}</Typography>

            <TextField
              size='small'
              fullWidth
              disabled={loading}
              value={address.zip ?? ''}
              onChange={(e) => setAddress({ ...address, zip: e.target.value })}
            />
          </Box>

          <FormControl>
            <FormLabel>{t('address.address_type')}</FormLabel>
            <RadioGroup
              value={address.addressType}
              onChange={(e) =>
                setAddress({ ...address, addressType: e.target.value })
              }
            >
              <FormControlLabel
                value='0'
                control={<Radio />}
                label={t('address.individual')}
              />
              <FormControlLabel
                value='1'
                control={<Radio />}
                label={t('address.company')}
              />
            </RadioGroup>
          </FormControl>

          {address.addressType === '1' && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  width: '100%',
                }}
              >
                <Typography>{t('address.tax_office')}</Typography>

                <TextField
                  size='small'
                  fullWidth
                  disabled={loading}
                  value={address.taxOffice ?? ''}
                  onChange={(e) =>
                    setAddress({ ...address, taxOffice: e.target.value })
                  }
                  error={!isTaxOfficeValid}
                  helperText={
                    isTaxOfficeValid
                      ? undefined
                      : t('validation.field_is_required')
                  }
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  width: '100%',
                }}
              >
                <Typography>{t('address.tax_number')}</Typography>

                <TextField
                  size='small'
                  fullWidth
                  disabled={loading}
                  value={address.taxNo ?? ''}
                  onChange={(e) =>
                    setAddress({ ...address, taxNo: e.target.value })
                  }
                  error={!isTaxNoValid}
                  helperText={
                    isTaxNoValid ? undefined : t('validation.field_is_required')
                  }
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={
                loading ||
                !isFirstNameValid ||
                !isLastNameValid ||
                !isAddressValid ||
                !isTaxNoValid ||
                !isTaxOfficeValid
              }
              sx={{
                textTransform: 'none',
                backgroundColor: primary,
                color: secondary,
              }}
            >
              {t('common.save')}
            </Button>

            {loading && <CircularProgress color='primary' />}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
